import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import { QUERY_KEYS } from "@/services/api/queryKeys";
import { MUTATION_KEYS } from "@/services/api/mutationKeys";
import type {
  SystemAdmin,
  SystemAdminResponse,
  CreateSystemAdminPayload,
  UpdateSystemAdminPayload,
} from "@/types";

export const useSystemAdmins = () => {
  const queryClient = useQueryClient();

  const adminsQuery = useQuery({
    queryKey: QUERY_KEYS.SYSTEM_ADMINS.LIST(),
    queryFn: async () => {
      const response = await apiClient.get<SystemAdmin[]>(
        API_ENDPOINTS.SYSTEM.ADMINS.BASE
      );
      return Array.isArray(response.data) ? response.data : [];
    },
  });

  const createAdminMutation = useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_SYSTEM_ADMIN],
    mutationFn: async (payload: CreateSystemAdminPayload) => {
      const response = await apiClient.post<SystemAdminResponse>(
        API_ENDPOINTS.SYSTEM.ADMINS.BASE,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SYSTEM_ADMINS.ALL,
      });
    },
  });

  const updateAdminMutation = useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_SYSTEM_ADMIN],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateSystemAdminPayload;
    }) => {
      const response = await apiClient.patch<SystemAdminResponse>(
        API_ENDPOINTS.SYSTEM.ADMINS.DETAIL(id),
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SYSTEM_ADMINS.ALL,
      });
    },
  });

  const deleteAdminMutation = useMutation({
    mutationKey: [MUTATION_KEYS.DELETE_SYSTEM_ADMIN],
    mutationFn: async (id: string) => {
      await apiClient.delete(API_ENDPOINTS.SYSTEM.ADMINS.DETAIL(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SYSTEM_ADMINS.ALL,
      });
    },
  });

  return {
    admins: adminsQuery.data ?? [],
    isLoading: adminsQuery.isLoading,
    error: adminsQuery.error,
    createAdmin: createAdminMutation.mutateAsync,
    isCreating: createAdminMutation.isPending,
    updateAdmin: updateAdminMutation.mutateAsync,
    isUpdating: updateAdminMutation.isPending,
    deleteAdmin: deleteAdminMutation.mutate,
    deleteAdminAsync: deleteAdminMutation.mutateAsync,
    isDeleting: deleteAdminMutation.isPending,
  };
};
